import React, { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const AboutCard = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleText1, setVisibleText1] = useState('');
  const [visibleText2, setVisibleText2] = useState('');
  const [visibleText3, setVisibleText3] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);
  
  const fullTexts = {
    text1: "At Ejil Enterprise LLC, we lead economic recovery with innovative technology. Our vision is to integrate IT and telecom systems, offering tailored solutions for clients in a dynamic work environment.",
    text2: "Our team consists of highly skilled professionals and technicians in the field of ICT. With a wealth of experience, we deliver managed services and support that ensure secure and efficient operations for businesses and government entities alike.",
    text3: "We ensure adherence to the highest standards of security and quality. Our ISO-certified facilities, strong backup systems, and focus on project excellence build trust and lasting partnerships."
}
;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const typeText = (text, setter, index) => {
    if (index <= text.length) {
      setter(text.slice(0, index));
    }
  };
  
  useEffect(() => {
    let mounted = true;
    let frame;
  
    const startParallelTyping = async () => {
      if (!isVisible || isTypingComplete) return;
  
      // Reset texts
      setVisibleText1('');
      setVisibleText2('');
      setVisibleText3('');
      
      await new Promise(resolve => setTimeout(resolve, 200));
  
      const maxLength = Math.max(
        fullTexts.text1.length,
        fullTexts.text2.length,
        fullTexts.text3.length
      );
  
      let currentIndex = 0;
  
      const animate = () => {
        if (!mounted || currentIndex > maxLength) {
          setIsTypingComplete(true);
          return;
        }
  
        typeText(fullTexts.text1, setVisibleText1, currentIndex);
        typeText(fullTexts.text2, setVisibleText2, currentIndex);
        typeText(fullTexts.text3, setVisibleText3, currentIndex);
  
        currentIndex++;
  
        if (currentIndex <= maxLength) {
          frame = requestAnimationFrame(() => {
            setTimeout(animate, 15);
          });
        } else {
          setIsTypingComplete(true);
        }
      };
  
      animate();
    };
  
    if (isVisible && !isTypingComplete) {
      startParallelTyping();
    }
  
    return () => {
      mounted = false;
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [isVisible, isTypingComplete]);

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('about-card');
      if (element) {
        const rect = element.getBoundingClientRect();
        const isFullyVisible = rect.top <= window.innerHeight * 0.8 && 
                             rect.bottom >= window.innerHeight * 0.2;
        
        if (isFullyVisible && !isVisible) {
          setIsVisible(true);
          setIsTypingComplete(false);
        } else if (!isFullyVisible && isVisible) {
          setIsVisible(false);
          setIsTypingComplete(false);
          setVisibleText1('');
          setVisibleText2('');
          setVisibleText3('');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isVisible]);

  const styles = {
    cardContainer: {
      width: '100%',
      maxWidth: '1200px',
      height: isMobile ? 'auto' : '500px',
      margin: '20px auto',
      position: 'relative',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      backgroundColor: 'white',
      fontFamily: 'Oswald, sans-serif',
    },
    bgImage: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      backgroundImage: `url('/map.jpg')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      filter: 'grayscale(100%)',
      zIndex: '1',
    },
    content: {
      position: 'relative',
      zIndex: '2',
      padding: isMobile ? '24px 20px' : '32px 40px',
      height: '100%',
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '30px' : '40px',
    },
    leftSection: {
      width: isMobile ? '100%' : '45%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0)' : 'translateX(-100%)',
      transition: 'all 1s ease',
    },
    rightSection: {
      width: isMobile ? '100%' : '65%',
      display: isMobile ? 'none' : 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      marginTop: '16px',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
      transition: 'all 1s ease',
    },
    mainTitle: {
      fontSize: isMobile ? '2.25rem' : '3.75rem',
      fontWeight: '600',
      color: '#414042',
      lineHeight: '72px',
      marginBottom: '16px',
      letterSpacing: '-0.02em',
      textAlign: 'left',
      fontFamily: 'Oswald, sans-serif',
    },
    subtitle: {
      fontSize: isMobile ? '1.15rem' : '1.75rem',
      color: '#410684',
      marginBottom: '24px',
      lineHeight: '40px',
      fontWeight: '500',
      maxWidth: isMobile ? '280px' : '320px',
      textAlign: 'left',
      fontFamily: 'Oswald, sans-serif',
    },
    button: {
      backgroundColor: '#410684',
      color: 'white',
      padding: '10px 20px',
      border: 'none',
      borderRadius: '4px',
      fontSize: '1.25rem',
      fontWeight: '500',
      cursor: 'pointer',
      width: 'fit-content',
      transition: 'background-color 0.3s ease',
      fontFamily: 'Oswald, sans-serif',
    },
    sectionTitle: {
      fontSize: '32px',
      fontWeight: '600',
      lineHeight: '40px',
      color: '#414042',
      marginBottom: '6px',
      textAlign: 'left',
      fontFamily: 'Oswald, sans-serif',
    },
    sectionText: {
      fontSize: '16px',
      color: '#414042',
      lineHeight: '24px',
      marginBottom: '16px',
      textAlign: 'left',
      position: 'relative',
      fontFamily: 'Inter, sans-serif',
    },
    cursor: {
      display: 'inline-block',
      width: '2px',
      height: '1em',
      backgroundColor: '#410684',
      marginLeft: '2px',
      animation: 'blink 1s infinite',
    },
    mapPinContainer: {
      position: 'absolute',
      left: '37%',
      top: '40%',
      transform: 'translate(-50%, -50%)',
      zIndex: '3',
      animation: 'floating 3s ease-in-out infinite',
      display: isMobile ? 'none' : 'block',
      opacity: isVisible ? 1 : 0,
      transition: 'opacity 1s ease',
    },
    pinCircle: {
      position: 'absolute',
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      backgroundColor: 'rgba(91, 33, 182, 0.1)',
      left: '40%',
      top: '40%',
      transform: 'translate(-50%, -50%)',
      animation: 'pulse 2s ease-in-out infinite',
    },
  };

  return (
    <div id="about-card" style={styles.cardContainer}>
      <div style={styles.bgImage}></div>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;500;600;700&display=swap');
          
          @keyframes floating {
            0% { transform: translate(-50%, -50%); }
            50% { transform: translate(-50%, -60%); }
            100% { transform: translate(-50%, -50%); }
          }
          @keyframes pulse {
            0% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
            50% { transform: translate(-50%, -50%) scale(1.2); opacity: 0.3; }
            100% { transform: translate(-50%, -50%) scale(1); opacity: 0.6; }
          }
          @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0; }
          }
        `}
      </style>
      <div style={styles.content}>
        <div style={styles.leftSection}>
          <h1 style={styles.mainTitle}>
            CONNECTING INNOVATION
          </h1>
          <h2 style={styles.subtitle}>
          EMPOWERING BUSINESSES WITH INNOVATIVE IT & TELECOM SOLUTIONS
          </h2>
          <button 
            style={styles.button}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4C1D95'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#5B21B6'}
            onClick={() => navigate('/about')}
          >
            LEARN MORE
          </button>
        </div>

        {!isMobile && (
          <>
            <div style={styles.mapPinContainer}>
              <div style={styles.pinCircle}></div>
              <MapPin size={32} color="#5B21B6" strokeWidth={2} />
            </div>
            <div style={styles.rightSection}>
              <div>
                <h3 style={styles.sectionTitle}>Our Vision</h3>
                <p style={styles.sectionText}>
                  {visibleText1}
                  {isVisible && !isTypingComplete && visibleText1.length < fullTexts.text1.length && <span style={styles.cursor}/>}
                </p>

                <h3 style={styles.sectionTitle}>Our Expertise</h3>
                <p style={styles.sectionText}>
                  {visibleText2}
                  {isVisible && !isTypingComplete && visibleText2.length < fullTexts.text2.length && <span style={styles.cursor}/>}
                </p>

                <h3 style={styles.sectionTitle}>Our Commitment</h3>
                <p style={styles.sectionText}>
                  {visibleText3}
                  {isVisible && !isTypingComplete && visibleText3.length < fullTexts.text3.length && <span style={styles.cursor}/>}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AboutCard;